import * as React from 'react';
import { useSelector } from 'react-redux';
import Checkbox from 'antd/lib/checkbox';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';

import { OrgUpdatable } from '@/actions/organization';
import { AgentRequest } from '@/actions/orgSettings';
import { platformLabelForApp } from '@/helpers/mdmPlatformOverride';
import { RootState } from '@/reducers/reducers';
import { selectUserAppDataByAppIdMap } from '@/selectors/selectors';
import { Agent, AgentType, RunwayResponseType, WebhookEndpoint } from '@/types';
import { useCreateAgentMutation } from '@/middlewares/defaultApi';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import {
    AgentCIWorkflowFields,
    CIWorkflowsFormValues,
    initialCIWorkflowFormValues,
    toCIWorkflowsRequest,
} from './AgentCIWorkflowFields';
import { AgentRoutineSetupModal } from './AgentRoutineSetupHelp';
import { DeliveryFormValues, toDeliveryRequest, DeliveryFields } from './AgentDeliveryFields';

enum AgentCategory {
    AgentService = 'agentService',
    CIWorkflow = 'ciWorkflow',
}

type AgentFormValues = DeliveryFormValues & {
    name: string;
    category: AgentCategory;
    provider: AgentType;
    appIds?: string[];
    ciWorkflows?: CIWorkflowsFormValues;
    routineFireUrl?: string;
    routineToken?: string;
    consentAcknowledged?: boolean;
};

const AGENT_SERVICE_PROVIDER_OPTIONS = [
    { value: AgentType.CustomWebhook, label: 'Standard webhook' },
    { value: AgentType.ClaudeRoutine, label: 'Claude Code routine' },
];

const CLAUDE_ROUTINE_FIRE_URL_PATTERN =
    /^https:\/\/api\.anthropic\.com\/v1\/claude_code\/routines\/trig_[A-Za-z0-9]{1,64}\/fire$/;

const agentTypeFromForm = (category: AgentCategory, provider: AgentType) =>
    category === AgentCategory.CIWorkflow ? AgentType.CIWorkflow : provider;

const RoutineFields: React.FC<{ hasSavedToken: boolean; onShowSetup: () => void }> = ({
    hasSavedToken,
    onShowSetup,
}) => (
    <>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Create the routine and its API trigger on claude.ai first.{' '}
            <Typography.Link onClick={onShowSetup}>See the setup steps</Typography.Link>
        </Text>
        <Form.Item
            name="routineFireUrl"
            label="Fire URL"
            extra="Shown when you add an API trigger to the routine."
            normalize={(value?: string) => value?.trim()}
            rules={[
                { required: true, message: 'Fire URL is required' },
                {
                    pattern: CLAUDE_ROUTINE_FIRE_URL_PATTERN,
                    message:
                        "Paste the URL of the routine's API trigger: https://api.anthropic.com/v1/claude_code/routines/trig_…/fire",
                },
            ]}
        >
            <Input placeholder="https://api.anthropic.com/v1/claude_code/routines/trig_…/fire" />
        </Form.Item>
        <Form.Item
            name="routineToken"
            label="Token"
            extra={
                hasSavedToken ?
                    'Saved. Leave it blank to keep it, or paste a new token.'
                :   'Generated with the API trigger. Runway stores it encrypted and never shows it again.'
            }
            rules={[{ required: !hasSavedToken, whitespace: true, message: 'Token is required' }]}
        >
            <Input.Password
                autoComplete="new-password"
                placeholder={hasSavedToken ? '••••••••••••' : 'sk-ant-oat01-…'}
                visibilityToggle={false}
            />
        </Form.Item>
    </>
);

interface AgentModalProps {
    orgId: string;
    agent?: Agent;
    webhook?: WebhookEndpoint;
    otherAgentNames: string[];
    onCreate: (agentName: string, agentType: AgentType) => void;
    onClose: () => void;
}

const AgentModal: React.FC<AgentModalProps> = ({ orgId, agent, webhook, otherAgentNames, onCreate, onClose }) => {
    const [form] = Form.useForm<AgentFormValues>();
    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, orgId));
    const initialType = agent?.type ?? AgentType.CustomWebhook;
    const initialCategory =
        initialType === AgentType.CIWorkflow ? AgentCategory.CIWorkflow : AgentCategory.AgentService;
    const initialProvider = initialType === AgentType.CIWorkflow ? AgentType.CustomWebhook : initialType;
    const category = Form.useWatch('category', form) ?? initialCategory;
    const agentType = agentTypeFromForm(category, Form.useWatch('provider', form) ?? initialProvider);
    const selectedAppIds = Form.useWatch('appIds', form) ?? agent?.appIds ?? [];
    const isCIWorkflow = agentType === AgentType.CIWorkflow;
    const isRoutine = agentType === AgentType.ClaudeRoutine;
    const [showRoutineSetup, setShowRoutineSetup] = React.useState(false);

    const [createAgent, { isLoading: createIsLoading }] = useCreateAgentMutation();
    const [updateAgent, updateIsLoading, updateResponseType, updateError] = useCallUpdatable(OrgUpdatable.UpdateAgent, {
        orgId,
    });

    React.useEffect(() => {
        if (updateResponseType === RunwayResponseType.Success) {
            globalMessage.success('Agent updated.');
            onClose();
        }
    }, [updateResponseType, onClose]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    const onFinish = async (values: AgentFormValues) => {
        const request: AgentRequest = {
            name: values.name.trim(),
            appIds: values.appIds ?? [],
            delivery: agentType === AgentType.CustomWebhook ? toDeliveryRequest(values) : undefined,
            ciWorkflows: isCIWorkflow ? toCIWorkflowsRequest(values.ciWorkflows, values.appIds ?? []) : undefined,
            routine:
                isRoutine ?
                    { fireUrl: values.routineFireUrl ?? '', token: values.routineToken?.trim() ?? '' }
                :   undefined,
        };
        if (agent) {
            updateAgent({ ...request, agentId: agent.id });
            return;
        }
        onCreate(request.name, agentType);
        const result = await createAgent({
            orgId,
            request: { ...request, type: agentType, consentAcknowledged: !!values.consentAcknowledged },
        });
        const response = 'data' in result ? result.data : undefined;
        if (response?.type === RunwayResponseType.Success) {
            onClose();
            return;
        }
        globalMessage.error(response?.message || 'Error creating agent');
    };

    return (
        <Modal
            open
            destroyOnClose
            title={agent ? 'Edit agent' : 'Create agent'}
            okText={agent ? 'Save' : 'Create'}
            okButtonProps={{ loading: createIsLoading || updateIsLoading }}
            onOk={() => form.submit()}
            onCancel={onClose}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    name: agent?.name ?? '',
                    category: initialCategory,
                    provider: initialProvider,
                    appIds: agent?.appIds ?? [],
                    ciWorkflows: initialCIWorkflowFormValues(agent),
                    routineFireUrl: agent?.routine?.fireUrl ?? '',
                    url: webhook?.url ?? '',
                }}
                onFinish={onFinish}
            >
                <Form.Item
                    name="name"
                    label="Name"
                    rules={[
                        { required: true, whitespace: true, message: 'Agent name is required' },
                        {
                            validator: (_, value: string) =>
                                otherAgentNames.includes((value ?? '').trim().toLowerCase()) ?
                                    Promise.reject('An agent with this name already exists')
                                :   Promise.resolve(),
                        },
                    ]}
                >
                    <Input placeholder="Claude" />
                </Form.Item>
                <Form.Item name="category" label="Type">
                    <Select
                        disabled={!!agent}
                        options={[
                            { value: AgentCategory.AgentService, label: 'Agent service' },
                            { value: AgentCategory.CIWorkflow, label: 'CI workflow' },
                        ]}
                    />
                </Form.Item>
                {category === AgentCategory.AgentService && (
                    <Form.Item name="provider" label="Provider">
                        <Select disabled={!!agent} options={AGENT_SERVICE_PROVIDER_OPTIONS} />
                    </Form.Item>
                )}
                <Form.Item
                    name="appIds"
                    label="Apps"
                    extra="The agent can only be assigned in these apps, and its API key only works for them."
                    rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one app' }]}
                >
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="Select apps"
                        optionFilterProp="label"
                        options={Object.values(appDataByAppIdMap).map((appData) => ({
                            value: appData.appId,
                            label: `${appData.appName} (${platformLabelForApp(appData)})`,
                        }))}
                    />
                </Form.Item>
                {isCIWorkflow ?
                    <AgentCIWorkflowFields orgId={orgId} appIds={selectedAppIds} />
                : isRoutine ?
                    <RoutineFields hasSavedToken={!!agent} onShowSetup={() => setShowRoutineSetup(true)} />
                :   <DeliveryFields />}
                {!agent && (
                    <Form.Item
                        name="consentAcknowledged"
                        valuePropName="checked"
                        style={{ marginTop: 24 }}
                        rules={[
                            {
                                validator: (_, checked: boolean) =>
                                    checked ? Promise.resolve() : Promise.reject('Confirm to create the agent'),
                            },
                        ]}
                    >
                        <Checkbox>
                            I understand that triage issue data, including crash reports, user feedback and ticket text,
                            will be sent to this agent and the service that runs it.
                        </Checkbox>
                    </Form.Item>
                )}
            </Form>
            {showRoutineSetup && (
                <AgentRoutineSetupModal agentName="the routine" onClose={() => setShowRoutineSetup(false)} />
            )}
        </Modal>
    );
};

export default AgentModal;
